import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {formatText} from 'common/util/format-text';

import {template} from './template';
import css from './index.styl?inline';

interface TextEditorJsxProps {
  attrs?: {
    text: string;
    placeholder?: string;
    'add-text-placeholder'?: string;
    'hide-footer'?: boolean;
    'icon-on-hover'?: boolean;
  };
}

interface TextEditStates {
  currText: string;
  prevText: string;
  isEditing: boolean;
  isChanged: boolean;
}
export class YATextEditor extends Component<TextEditStates, TextEditorJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<TextEditorJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      text: `string`,
      placeholder: `string`,
      'add-text-placeholder': `string`,
      'hide-footer': {
        type: `boolean`,
        description: `minimize the editor ui by hiding save and cancel buttons`,
      },
      'icon-on-hover': {
        type: `boolean`,
        description: `it will only display icon on hover`,
      },
    };
  }

  get config(): ConfigOptions<TextEditStates> {
    return {
      useShadowDom: true,
      css,
      defaultState: {
        currText: ``,
        prevText: ``,
        isChanged: false,
        isEditing: false,
      },
      template,
      helpers: {
        formatText,
        handleEditingStart: () => {
          this.update({isEditing: true});
        },
        handleValueChange: (ev) => {
          if (this.state.isEditing) {
            this.update({currText: ev.target.value});
          }
        },

        handleInputInserted: ({elm}) => {
          if (this.state.isEditing) {
            elm.focus();
          }
        },

        handleSaveByKeyPress: (ev) => {
          if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER && !ev.shiftKey && this.attr(`hide-footer`)) {
            this.saveText();
          }
        },

        handleSaveByBlur: () => {
          if (this.attr(`hide-footer`)) {
            this.saveText();
          }
        },

        textChanged: () => this.state.currText !== this.state.prevText,

        handleSaveByButton: () => {
          this.saveText();
        },

        handleCancelEdit: () => {
          this.update({isEditing: false, currText: this.state.prevText});
        },
      },
    };
  }

  saveText() {
    if (this.helpers.textChanged()) {
      this.dispatchEvent(
        new CustomEvent(`changedText`, {
          detail: {value: this.state.currText},
        }),
      );
    }
    this.update({isEditing: false, prevText: this.state.currText});
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `text`) {
      this.update({currText: this.attr(`text`), prevText: this.attr(`text`)});
    }
  }

  connectedCallback() {
    super.connectedCallback();
  }
}

export const YATextEditEl = defineAndMakeJsxFactory<TextEditorJsxProps>(`mp-ya-text-editor`, YATextEditor);
